/**
 * BusinessCajaTotal — "Tu caja total": FlowexPay + los bancos de la empresa.
 *
 * El banco te muestra un numero muerto; aqui ves TODA tu caja, cuanta esta quieta al 0%
 * y cuanto llevas de 4x1000 (con su 50% deducible). Los bancos entran subiendo el
 * EXTRACTO (PDF/foto/CSV) — NUNCA pedimos claves del banco.
 * Visual: .caja-ola glass Ola A.
 *
 * Flujo: subir -> Claude lee (20-60s) -> revision humana -> confirmar -> queda en la caja.
 */
import { useEffect, useRef, useState } from 'react';
import { getCajaTotal, leerExtractoBancario, confirmarExtractoBancario } from '../../lib/cerebro';

const cop = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-CO');
const usd = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function BusinessCajaTotal() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [leyendo, setLeyendo] = useState(false);
  const [draft, setDraft] = useState(null);      // lo que leyo la IA, esperando confirmacion
  const [resumen, setResumen] = useState('');
  const [archivo, setArchivo] = useState(null);  // { base64, mediaType, nombre }
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const cargar = () => {
    getCajaTotal()
      .then((d) => setData(d?.sinEmpresa ? null : d))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  };
  useEffect(cargar, []);

  const onArchivo = async (file) => {
    if (!file) return;
    setError(null); setMsg(null); setDraft(null);
    if (file.size > 7 * 1024 * 1024) { setError('El archivo es muy pesado (máximo 7 MB). Sube un extracto más liviano.'); return; }
    setLeyendo(true);
    try {
      const esTexto = /\.(csv|txt)$/i.test(file.name);
      let r;
      if (esTexto) {
        const texto = await file.text();
        r = await leerExtractoBancario(null, null, texto);
        setArchivo({ base64: null, mediaType: 'text/plain', nombre: file.name });
      } else {
        const base64 = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(String(fr.result).split(',')[1]);
          fr.onerror = rej;
          fr.readAsDataURL(file);
        });
        const mediaType = file.type || (/\.pdf$/i.test(file.name) ? 'application/pdf' : 'image/jpeg');
        r = await leerExtractoBancario(base64, mediaType);
        setArchivo({ base64, mediaType, nombre: file.name });
      }
      if (r?.ok) { setDraft(r.draft); setResumen(r.resumen || ''); }
      else setError(r?.mensaje || 'No pude leer el extracto. Intenta con un archivo más nítido.');
    } catch (e) {
      setError('No pude leer el extracto. Intenta de nuevo.');
    } finally {
      setLeyendo(false);
    }
  };

  const confirmar = async () => {
    setLeyendo(true);
    try {
      const r = await confirmarExtractoBancario(draft, archivo?.base64, archivo?.mediaType, archivo?.nombre);
      setMsg(r?.mensaje || 'Listo, quedó en tu caja total.');
      setDraft(null); setArchivo(null);
      cargar();
    } catch {
      setError('No se pudo guardar el extracto. Intenta de nuevo.');
    } finally {
      setLeyendo(false);
    }
  };

  const bancos = data?.bancos || [];
  const gmf = data?.gmf || {};

  return (
    <section className="caja-ola__panel">
      <div className="caja-ola__panel-top">
        <div>
          <h2 className="caja-ola__h2">Tu caja total</h2>
          <p className="caja-ola__hint">Lo que tienes en FlowexPay más lo que tienes en tus bancos.</p>
        </div>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={leyendo} className="caja-ola__btn caja-ola__btn--primary">
          {leyendo ? 'Leyendo…' : '+ Sube tu extracto'}
        </button>
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.csv,.txt" className="hidden"
          onChange={(e) => { onArchivo(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      {cargando ? (
        <p className="caja-ola__hint mt">Cargando tu caja…</p>
      ) : (
        <>
          {/* Tarjetas de saldo */}
          <div className="caja-ola__cards">
            <div className="caja-ola__metric caja-ola__metric--good">
              <div className="caja-ola__metric-lbl">En FlowexPay · al segundo</div>
              <div className="caja-ola__metric-val">{cop(data?.flowexpay?.balanceCop)} <span>COP</span></div>
              <div className="caja-ola__metric-sub">{usd(data?.flowexpay?.balanceUsd)} USD</div>
            </div>
            {bancos.map((b, i) => (
              <div key={i} className="caja-ola__metric">
                <div className="caja-ola__metric-top">
                  <div className="caja-ola__metric-lbl truncate">{b.banco} {b.cuentaMask}</div>
                  {b.desactualizado && <span className="caja-ola__pill caja-ola__pill--warn">Actualiza</span>}
                </div>
                <div className="caja-ola__metric-val">{b.moneda === 'USD' ? usd(b.saldo) : cop(b.saldo)} <span>{b.moneda}</span></div>
                <div className="caja-ola__metric-sub">Extracto del {fecha(b.fechaCorte)} · hace {b.diasDesdeCorte} días</div>
              </div>
            ))}
            {bancos.length === 0 && !draft && (
              <div className="caja-ola__metric caja-ola__metric--dash">
                <p className="caja-ola__hint">Sube el extracto de tu banco y verás aquí toda tu caja junta.</p>
              </div>
            )}
          </div>

          {/* Metricas que educan */}
          {(data?.totales?.plataQuietaCop > 0 || gmf.anual > 0) && (
            <div className="caja-ola__cards mt">
              {data?.totales?.plataQuietaCop > 0 && (
                <div className="caja-ola__metric caja-ola__metric--bad">
                  <div className="caja-ola__metric-lbl">Plata quieta</div>
                  <div className="caja-ola__metric-val">{cop(data.totales.plataQuietaCop)}</div>
                  <div className="caja-ola__metric-sub">en bancos, rindiendo 0%</div>
                </div>
              )}
              {gmf.anual > 0 && (
                <div className="caja-ola__metric caja-ola__metric--blue">
                  <div className="caja-ola__metric-lbl">4×1000 este año</div>
                  <div className="caja-ola__metric-val">{cop(gmf.anual)}</div>
                  <div className="caja-ola__metric-sub">el 50% ({cop(gmf.deducible50)}) es deducible de renta — díselo a tu contador</div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Estado de lectura */}
      {leyendo && !draft && (
        <div className="caja-ola__banner caja-ola__banner--muted mt">
          <p className="caja-ola__banner-strong">Leyendo tu extracto…</p>
          <p className="caja-ola__hint">Esto toma menos de un minuto. No cierres esta página.</p>
        </div>
      )}
      {error && <p className="caja-ola__err mt">{error}</p>}
      {msg && <p className="caja-ola__ok mt">{msg}</p>}

      {/* Revision humana antes de guardar */}
      {draft && (
        <div className="caja-ola__draft mt">
          <p className="caja-ola__banner-strong">Esto fue lo que leí — revísalo</p>
          <p className="caja-ola__hint mb">{resumen}</p>
          <div className="caja-ola__draft-grid">
            <div><div className="caja-ola__metric-lbl">Banco</div><div className="caja-ola__draft-val">{draft.banco} {draft.cuentaMask}</div></div>
            <div><div className="caja-ola__metric-lbl">Saldo de cierre</div><div className="caja-ola__draft-val">{draft.moneda === 'USD' ? usd(draft.saldoCierre) : cop(draft.saldoCierre)}</div></div>
            <div><div className="caja-ola__metric-lbl">Fecha de corte</div><div className="caja-ola__draft-val">{draft.fechaCorte || '—'}</div></div>
            <div><div className="caja-ola__metric-lbl">4×1000 del periodo</div><div className="caja-ola__draft-val">{draft.gmfParcial ? 'al menos ' : ''}{cop(draft.gmfTotal)}</div></div>
          </div>
          {(draft.movimientos || []).length > 0 && (
            <div className="caja-ola__movs">
              {draft.movimientos.slice(0, 6).map((m, i) => (
                <div key={i} className="caja-ola__mov">
                  <span className="caja-ola__mov-desc">
                    {m.esGmf && <span className="caja-ola__pill caja-ola__pill--blue">4×1000</span>}
                    {m.descripcion}
                  </span>
                  <span className={`caja-ola__mov-amt${m.monto < 0 ? ' is-out' : ' is-in'}`}>{cop(m.monto)}</span>
                </div>
              ))}
              {draft.movimientos.length > 6 && (
                <div className="caja-ola__hint px">y {draft.movimientos.length - 6} movimientos más…</div>
              )}
            </div>
          )}
          <div className="caja-ola__cta-row">
            <button type="button" onClick={confirmar} disabled={leyendo} className="caja-ola__btn caja-ola__btn--primary">{leyendo ? 'Guardando…' : 'Sí, es correcto — guardar'}</button>
            <button type="button" onClick={() => { setDraft(null); setArchivo(null); }} className="caja-ola__btn caja-ola__btn--ghost">Cancelar</button>
          </div>
        </div>
      )}

      <p className="caja-ola__foot">
        Nunca te pedimos las claves de tu banco. Solo lees tú: subes tu extracto (PDF, foto o CSV) y nosotros lo entendemos.
      </p>
    </section>
  );
}
